import { createClient } from '@/lib/supabase/client';

export interface PerformanceWarning {
  id: string;
  type: 'critical' | 'warning';
  category: 'grade_drop' | 'ontime_rate' | 'violation_pattern';
  crewId: string;
  crewName: string;
  crewCode: string;
  title: string;
  detail: string;
  metric: string;
  value: number;
  threshold: number;
  detectedAt: Date;
  actionHref: string;
}

// Thresholds
const GRADE_DROP_THRESHOLD = 'C'; // Grade C or below triggers warning
const GRADE_CRITICAL_THRESHOLD = 'D'; // Grade D or F triggers critical
const ONTIME_RATE_WARNING = 75; // below 75% on-time = warning
const ONTIME_RATE_CRITICAL = 60; // below 60% on-time = critical
const VIOLATION_WARNING_COUNT = 2; // 2+ violations in 30 days = warning
const VIOLATION_CRITICAL_COUNT = 4; // 4+ violations in 30 days = critical

const GRADE_ORDER: Record<string, number> = { A: 5, B: 4, C: 3, D: 2, F: 1 };

function gradeScore(grade: string): number {
  return GRADE_ORDER[grade?.toUpperCase()] ?? 0;
}

export async function detectPerformanceWarnings(): Promise<PerformanceWarning[]> {
  const supabase = createClient();
  const warnings: PerformanceWarning[] = [];

  // --- 1. Grade Drop Detection ---
  // Get crew members with grade C, D, or F
  const { data: lowGradeCrew } = await supabase
    .from('crew_members')
    .select('id, name, crew_code, grade, role, status')
    .in('grade', ['C', 'D', 'F'])
    .eq('status', 'Available');

  (lowGradeCrew ?? []).forEach((crew) => {
    const score = gradeScore(crew.grade);
    const isCritical = score <= gradeScore(GRADE_CRITICAL_THRESHOLD);
    warnings.push({
      id: `grade-${crew.id}`,
      type: isCritical ? 'critical' : 'warning',
      category: 'grade_drop',
      crewId: crew.id,
      crewName: crew.name,
      crewCode: crew.crew_code,
      title: `Grade Drop: ${crew.name}`,
      detail: `${crew.role} ${crew.crew_code} has grade ${crew.grade} — below acceptable threshold`,
      metric: 'Performance Grade',
      value: score,
      threshold: gradeScore(GRADE_DROP_THRESHOLD),
      detectedAt: new Date(),
      actionHref: '/duty-history-performance',
    });
  });

  // --- 2. On-Time Rate Detection (from post_duty_feedback) ---
  // Get feedback from last 30 days grouped by crew
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const { data: feedbackData } = await supabase
    .from('post_duty_feedback')
    .select('crew_id, crew_name, actual_departure_time, scheduled_departure_time, delay_minutes')
    .gte('created_at', thirtyDaysAgo.toISOString());

  if (feedbackData && feedbackData.length > 0) {
    // Group by crew
    const crewFeedbackMap: Record<string, { name: string; total: number; onTime: number }> = {};
    feedbackData.forEach((fb) => {
      const key = fb.crew_id ?? fb.crew_name;
      if (!key) return;
      if (!crewFeedbackMap[key]) {
        crewFeedbackMap[key] = { name: fb.crew_name ?? key, total: 0, onTime: 0 };
      }
      crewFeedbackMap[key].total += 1;
      const delayMin = Number(fb.delay_minutes ?? 0);
      if (delayMin <= 5) crewFeedbackMap[key].onTime += 1;
    });

    Object.entries(crewFeedbackMap).forEach(([crewId, stats]) => {
      if (stats.total < 3) return; // Need at least 3 trips for meaningful rate
      const onTimeRate = Math.round((stats.onTime / stats.total) * 100);
      if (onTimeRate < ONTIME_RATE_WARNING) {
        const isCritical = onTimeRate < ONTIME_RATE_CRITICAL;
        warnings.push({
          id: `ontime-${crewId}`,
          type: isCritical ? 'critical' : 'warning',
          category: 'ontime_rate',
          crewId,
          crewName: stats.name,
          crewCode: '',
          title: `Low On-Time Rate: ${stats.name}`,
          detail: `${onTimeRate}% on-time over last ${stats.total} duties (threshold: ${ONTIME_RATE_WARNING}%)`,
          metric: 'On-Time Rate',
          value: onTimeRate,
          threshold: ONTIME_RATE_WARNING,
          detectedAt: new Date(),
          actionHref: '/duty-history-performance',
        });
      }
    });
  }

  // --- 3. Violation Pattern Detection (last 30 days) ---
  const { data: recentViolations } = await supabase
    .from('violations')
    .select('crew_id, crew_name, violation_date, category, status')
    .gte('violation_date', thirtyDaysAgo.toISOString().split('T')[0]);

  if (recentViolations && recentViolations.length > 0) {
    const violationMap: Record<string, { name: string; count: number; categories: string[] }> = {};
    recentViolations.forEach((v) => {
      const key = v.crew_id ?? v.crew_name;
      if (!key) return;
      if (!violationMap[key]) {
        violationMap[key] = { name: v.crew_name ?? key, count: 0, categories: [] };
      }
      violationMap[key].count += 1;
      if (v.category && !violationMap[key].categories.includes(v.category)) {
        violationMap[key].categories.push(v.category);
      }
    });

    Object.entries(violationMap).forEach(([crewId, stats]) => {
      if (stats.count >= VIOLATION_WARNING_COUNT) {
        const isCritical = stats.count >= VIOLATION_CRITICAL_COUNT;
        const catSummary = stats.categories.slice(0, 2).join(', ');
        warnings.push({
          id: `violation-${crewId}`,
          type: isCritical ? 'critical' : 'warning',
          category: 'violation_pattern',
          crewId,
          crewName: stats.name,
          crewCode: '',
          title: `Violation Pattern: ${stats.name}`,
          detail: `${stats.count} violation${stats.count > 1 ? 's' : ''} in 30 days${catSummary ? ` — ${catSummary}` : ''}`,
          metric: 'Violations (30d)',
          value: stats.count,
          threshold: VIOLATION_WARNING_COUNT,
          detectedAt: new Date(),
          actionHref: '/violations',
        });
      }
    });
  }

  // Deduplicate: if same crew has both grade + violation warning, keep both but avoid duplicate IDs
  const seen = new Set<string>();
  return warnings.filter((w) => {
    if (seen.has(w.id)) return false;
    seen.add(w.id);
    return true;
  });
}

export function warningToActivityEvent(w: PerformanceWarning) {
  const iconMap: Record<PerformanceWarning['category'], string> = {
    grade_drop: '📉',
    ontime_rate: '⏱️',
    violation_pattern: '🚨',
  };
  const colorMap: Record<PerformanceWarning['type'], string> = {
    critical: 'bg-red-100 text-red-700',
    warning: 'bg-amber-100 text-amber-700',
  };
  return {
    id: `perf-warn-${w.id}`,
    icon: iconMap[w.category],
    colorClass: colorMap[w.type],
    message: `⚠️ ${w.title}: ${w.detail}`,
    timestamp: w.detectedAt,
  };
}
